/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { EXCEPTION_INVALID_ARGUMENT, NAME_CULTURE_INFO_FORMATTER_EXCEPTION } from '@msdyn365-commerce/telemetry-internal';
import memoizeFormatConstructor from 'intl-format-cache';
import areIntlLocalesSupported from 'intl-locales-supported';
import { IDictionary } from '@msdyn365-commerce/retail-proxy';
import { ICultureInfoFormatter, IDateFormatOptions, INumberFormatOptions, ITimeFormatOptions } from '../interfaces';

const numericIdentifier = 'numeric';

/**
 * CultureInfoFormatter provides functions that support the localized formatting
 * of currencies, numbers, dates and times.
 */
export class CultureInfoFormatter implements ICultureInfoFormatter {
    private locale: string;
    private numberFormatter: Intl.NumberFormat | undefined;
    private dateFormatter: Intl.DateTimeFormat | undefined;
    private timeFormatter: Intl.DateTimeFormat | undefined;
    private currencyFormatter: Intl.NumberFormat | undefined;
    private numFormatterConstructor: any;
    private dateFormatterConstructor: any;
    private currencyCode: string;
    // Adding a cultureInfoTestFlag to inject intl on client side for culture-intl-formatter test, since now the Intl is injected through script tage in base-page.tsx
    private queryString: IDictionary<string> | undefined;

    private static _validateInput(validation: boolean, methodName: string, argName: string): void {
        if (validation) {
            throw new Error(`${NAME_CULTURE_INFO_FORMATTER_EXCEPTION}: ${EXCEPTION_INVALID_ARGUMENT(methodName, argName)}`);
        }
    }

    /**
     * Constructs a new instance of this formatter using the given locale.
     * Default value for locale is 'en-US'
     *
     * @param locale A string containing a BCP-47 locale tag e.g. 'en', 'en-us', 'fr-fr'. Default value of 'en-US'
     * For more information see https://tools.ietf.org/html/bcp47
     */
    constructor(locale: string, currencyCode: string, queryString?: IDictionary<string>) {
        CultureInfoFormatter._validateInput(!locale, 'CultureInfoFormatter', 'locale');
        CultureInfoFormatter._validateInput(!currencyCode, 'CultureInfoFormatter', 'currencyCode');

        this.locale = locale;
        this.currencyCode = currencyCode;
        this.queryString = queryString;
        // If we are running server side or the browser does not support the locale, use the polyfill for Intl

        const loadIntlPollyfillQsp = this.queryString && this.queryString.load_intl_polyfills;
        function loadIntlPollyfills(IntlPolyfill?: any) {
            // @ts-ignore- IntlPolyfill are loaded through script tag
            Intl.NumberFormat = IntlPolyfill.NumberFormat;
            // @ts-ignore- IntlPolyfill are loaded through script tag
            Intl.DateTimeFormat = IntlPolyfill.DateTimeFormat;
        }

        if (process.env.CURRENT_ENVIRONMENT === 'node') {
            // eslint-disable-next-line
            const IntlPolyfill = require('intl');
            loadIntlPollyfills(IntlPolyfill);
        } else {
            if (!areIntlLocalesSupported(this.locale) || loadIntlPollyfillQsp) {
                if (process.env.JEST_WORKER_ID !== undefined) {
                    // @ts-ignore - IntlPolyfill var is defined only while executing culture-intl-formatter test
                    // eslint-disable-next-line
                    const IntlPolyfill = require('intl');
                    loadIntlPollyfills(IntlPolyfill);
                } else {
                    // @ts-ignore - IntlPolyfill is loaded through script tag
                    loadIntlPollyfills(IntlPolyfill);
                }
            }
        }
        // Create cached number, date and time formatter for basic formatting
        this.numFormatterConstructor = memoizeFormatConstructor(Intl.NumberFormat);
        this.dateFormatterConstructor = memoizeFormatConstructor(Intl.DateTimeFormat);
    }

    /**
     * Returns a localized currency formatted version of a price.
     *
     * @param price Either a string or number representing the price that will be localized and formatted
     * @param currencyCode Optional argument. The three letter currency code that will be used for formatting the currency.
     * If the currency code is not provided the locale will be used to determine the best fit currency code.
     */
    public formatCurrency: (price: string | number, currencyCode?: string) => string = (
        price: string | number,
        currencyCode?: string
    ): string => {
        CultureInfoFormatter._validateInput(price === undefined, 'formatCurrency', 'price');
        price = price || 0;

        try {
            if (!this.currencyFormatter) {
                this.currencyFormatter = <Intl.NumberFormat>this.numFormatterConstructor(this.locale, {
                    style: 'currency',
                    currency: currencyCode || this.currencyCode
                });
            }
            return !currencyCode
                ? this.currencyFormatter.format(+price)
                : this.formatNumber(+price, { style: 'currency', currency: currencyCode });
        } catch (e) {
            console.error('error in creating number formatter', e);
            // return the same unformatted value
            return price.toString();
        }
    };

    /**
     * Returns a localized formatted version of a number
     *
     * @param value The number that will be localized and formatted
     * @param options An optional argument that controls the formatting.
     */
    public formatNumber = (value: number, options?: INumberFormatOptions): string => {
        CultureInfoFormatter._validateInput(value === undefined, 'formatNumber', 'value');
        value = value || 0;

        try {
            if (!this.numberFormatter) {
                this.numberFormatter = <Intl.NumberFormat>this.numFormatterConstructor(this.locale);
            }
            // If no options are given to us, we can use the basic cached formatter
            return options !== undefined
                ? this.numFormatterConstructor(this.locale, options).format(value)
                : this.numberFormatter.format(value);
        } catch (e) {
            console.error('error creating number formatter', e);
            // return the same unformatted value
            return value.toString();
        }
    };

    /**
     * Returns a localized formatted version of a date
     *
     * @param date Date object or valid date string representing the date that will be localized and formatted
     * @param options An optional argument that controls the formatting.
     */
    public formatDate = (date: Date | string, options?: IDateFormatOptions): string => {
        CultureInfoFormatter._validateInput(!date, 'formatDate', 'date');

        try {
            if (!this.dateFormatter) {
                this.dateFormatter = <Intl.DateTimeFormat>this.dateFormatterConstructor(this.locale);
            }

            // Convert to date object if valid date string is passed instead
            if (typeof date === 'string') {
                date = new Date(date);
            }

            // If no options are given to us, we can use the basic cached formatter
            return options !== undefined
                ? this.dateFormatterConstructor(this.locale, options).format(date)
                : this.dateFormatter.format(date);
        } catch (e) {
            console.error('error creating date formatter', e);
            // return the same unformatted value
            return date.toString();
        }
    };

    /**
     * Returns a localized formatted version of a time
     *
     * @param time Date object or valid date string representing the time that will be localized and formatted
     * @param options An optional argument that controls the formatting
     */
    public formatTime = (time: Date | string, options?: ITimeFormatOptions): string => {
        CultureInfoFormatter._validateInput(!time, 'formatTime', 'time');

        try {
            if (!this.timeFormatter) {
                this.timeFormatter = <Intl.DateTimeFormat>(
                    this.dateFormatterConstructor(this.locale, { hour: numericIdentifier, minute: numericIdentifier })
                );
            }

            // Convert to date object if valid date string is passed instead
            if (typeof time === 'string') {
                time = new Date(time);
            }

            // If no options are given to us, we can use the basic cached formatter
            if (options !== undefined) {
                options.hour = 'numeric';
                options.minute = 'numeric';
                return this.dateFormatterConstructor(this.locale, options).format(time);
            }
            return this.timeFormatter.format(time);
        } catch (e) {
            console.error('error creating date formatter', e);
            // return the same unformatted value
            return time.toString();
        }
    };
}
